import {ChangeDetectorRef, Component, NgZone, OnInit, TemplateRef, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {Contract} from '../../../models/contract.model';
import {lastValueFrom, map, Observable} from 'rxjs';
import {ContractService} from '../../../services/model/contract.service';
import {MyStorageService} from "../../../services/my-storage.service";
import {PageTest} from "../../../utilities/PageTest";
import {AdminLayoutComponent} from "../../../layouts/admin-layout/admin-layout.component";
import {ROUTING} from "../../../utilities/routing-constants";
import {MarkerElement} from "../../../components/map/map.component";
import {VIEW_CHANGER_CONTAINER, ViewChanger} from "../../../components/list-view-header/list-view-header.component";
import {TableAction, TableCol} from "ng-aquila-datatable";
import {AbstractListPage} from "../../../models/AbstractListPage";
import {NxViewportService} from "@allianz/ng-aquila/utils";
import {AbstractCaptionService} from "irf-caption";
import HU_LOCALE from "apexcharts/dist/locales/hu.json";
import {Utilities} from "../../../utilities/utilities";
import {NxDialogService, NxModalRef} from "@allianz/ng-aquila/modal";
import {
  AddContractToModelComponent
} from "../../../components/modal/add-contract-to-model/add-contract-to-model.component";
import {ApexOption, GantChartComponent} from "../../../components/gant-chart/gant-chart.component";
import {
  SelectContractEditTypeComponent
} from "../../../components/modal/select-contract-edit-type/select-contract-edit-type.component";
import {ContractGANTFilter} from "../../../services/model/base-contract.service";
import {LoaderService} from "../../../services/loader.service";
import {GANT_FILTER_COOKIE_KEY} from "../../../utilities/constants";


@Component({
    selector: 'app-contracts',
    templateUrl: './contracts.component.html',
    styleUrls: ['./contracts.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
@PageTest({
  path: ROUTING.Contract.basePlural,
  layout: AdminLayoutComponent
})
export class ContractsComponent extends AbstractListPage<Contract[], ContractService> implements OnInit {

  gantModels: Contract[];
  mapModels: MarkerElement[];
  colDef: TableCol[] = [];
  actionDef: TableAction[];
  componentDialogRef?: NxModalRef<any>;
  headerTitle = "contract.headline";

  @ViewChild('test') hdrTpl: TemplateRef<any>;
  @ViewChild('gantChartComponent') chart: GantChartComponent;

  viewChangers: VIEW_CHANGER_CONTAINER;
  gantFilter: ContractGANTFilter = new ContractGANTFilter();
  onlyActive = true;
  tableActiveAt: Date | null = null;

  constructor(viewportService: NxViewportService, cdr: ChangeDetectorRef, service: ContractService,
              translateService: AbstractCaptionService, myStorageService: MyStorageService,
              private ngZone: NgZone,
              private loaderService: LoaderService,
              public dialogService: NxDialogService,) {
    super(viewportService, cdr, translateService, service, myStorageService);

    const viewFromCookies = this.myStorageService.getFromCookies(this.headerTitle, 'calendarView');
    this.viewChangers = {
      'calendarView': new ViewChanger('calendarView', viewFromCookies === 'calendarView', 'calendar'),
      'tableView': new ViewChanger('tableView', viewFromCookies === 'tableView', 'table'),
    }
    this.eventWhereMobileIsTrue = () => {
      if (this.mobile) {
        this.viewChangers['calendarView'].show = false;
        this.viewChangers['tableView'].show = true;
      } else {
        const updatedViewFromCookies = this.myStorageService.getFromCookies(this.headerTitle, 'calendarView');
        this.viewChangers['calendarView'].show = updatedViewFromCookies === 'calendarView';
        this.viewChangers['tableView'].show = updatedViewFromCookies === 'tableView';
      }
    }
  }

  ngOnInit(): void {
    this.loadData();
  }

  markerClick(): (event: any) => void {
    return (e) => this.service.show(e.target.options.markerId);
  }

  chartClick(): (event, chartContext, config) => void {
    return (event, chartContext, config) => {
      const series: any = this.options.series[config.seriesIndex]
      setTimeout(() => {
        this.ngZone.run(() => {
          this.service.show(series.data[config.dataPointIndex].meta.id);
        });
      }, 10);
    };
  }

  private async loadData(): Promise<void> {
    this.loaderService.show();
    this.actionDef = [
      new TableAction('eye', this.show(), await this.getTranslate('table.action.show')),
      new TableAction('pencil', this.edit(), await this.getTranslate('table.action.edit'), this.rowPermission()),
      new TableAction('trash', this.delete(), await this.getTranslate('table.action.delete'), this.isDeletable()),
      new TableAction('unlock', this.unlock(), await this.getTranslate('table.action.unlock'), this.isLock()),
    ];
    this.colDef = [
      TableCol.newString('id', await this.getTranslate('general.id')),
      TableCol.newString('partner.name', await this.getTranslate('contract.partner')),
      TableCol.newString('name', await this.getTranslate('contract.name')),
      TableCol.newDate('start', await this.getTranslate('contract.start')),
      TableCol.newDate('end', await this.getTranslate('contract.end')),
    ];
    this.loadContracts();
    this.loaderService.hide();
  }

  private loadContracts(): void {
    let source$: Observable<Contract[]>;
    if (this.onlyActive) {
      source$ = this.service.getAllActive();
    } else if (this.tableActiveAt) {
      source$ = this.service.getAllActiveAt(Utilities.dateToBackendString(new Date(this.tableActiveAt)));
    } else {
      source$ = this.service.getAllByDeleted(false);
    }
    source$.pipe(
      map(models => this.service.addExtendedPropertiesForModelList(models))
    ).subscribe(models => {
      this.models = models;
      this.gantModels = models;
      this._cdr.detectChanges();
    });
  }


  override edit(): (row: any) => void {
    return (contract: any) => {

      this.componentDialogRef = this.dialogService.open(SelectContractEditTypeComponent,
        Utilities.getDefaultModalConfig({
          model: contract
        }));
      this.componentDialogRef.afterClosed().subscribe(result => {
        if (result != 'cancel') {
          this.service.edit(contract.id, result);
        }
        this.componentDialogRef = undefined;
      });
    };

  }

  private delete(): (row: any) => void {
    return async (row: any) => {
      this.service.notificationService.confirm('Figyelmeztetés', 'Biztos hogy törlöd?', 'general.ok', 'general.cancel').then(async result => {
        if (result.value) {
          await lastValueFrom(this.service.repo.deleteById(row.id));
          this.loadData();
        }
      });
    }
  }

  openAddContractModal(): (event, chartContext, config) => void {
    return (event, chartContext, config) => {
      if (this.hasPermission) {
        const labelName = config.globals.labels[config.labelIndex];
        let dates: Date[] = [];
        const contracts = this.models.filter(contract => {
          const result = contract.measuringPoint?.name == labelName;
          if (result) {
            dates = dates.concat(Utilities.getDatesInRange(new Date(contract.start), new Date(contract.end)));
          }
          return result
        });

        if (contracts.length > 0) {
          const measuringPoint = contracts[0].measuringPoint;
          this.ngZone.run(() => {
            this.componentDialogRef = this.dialogService.open(AddContractToModelComponent,
              Utilities.getDefaultModalConfig({
                model: new Contract(measuringPoint),
                denyDates: dates
              }));
            this.componentDialogRef.afterClosed().subscribe(result => {
              if (result != 'cancel') {
                this.saveProcess(result);
              }
              this.componentDialogRef = undefined;
            });
          });
        } else {
          this.service.notificationService.showWarning('general.warning', 'error.measuringPointIsUndefined');
        }
      }
    };
  }

  options: ApexOption = {
    series: [],
    tooltip: {
      x: {
        show: true
      }
    },
    chart: {
      locales: [HU_LOCALE],
      defaultLocale: 'hu',
      height: 750,
      type: 'rangeBar',
      zoom: {allowMouseWheelZoom: false},
      events: {
        dataPointSelection: this.chartClick(),
        xAxisLabelClick: this.openAddContractModal()
      },
      toolbar: {
        show: true,
        offsetX: 0,
        offsetY: 0,
        tools: {
          download: true,
          selection: true,
          zoom: false,
          zoomin: true,
          zoomout: true,
          pan: true
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: "80%",

        rangeBarGroupRows: true
      }
    },
    fill: {},
    dataLabels: {
      enabled: true,

      formatter: (val, opts) => {
        const series: any = this.options.series[opts.seriesIndex];
        const contract: Contract = series.data[opts.dataPointIndex].meta;
        return contract.measuringDevice?.uuId + ' (' + contract.partner?.name + ')';
      }
    },
    xaxis: {
      type: "datetime",
      labels: {
        datetimeFormatter: {
          year: 'yyyy',
          month: "yyyy MMM",
          day: 'MMM dd',
          hour: 'HH:mm',
        },
      }
    },
    yaxis: {
      labels: {
        style: {
          cssClass: 'apexcharts-yaxis-label'
        }
      }
    },
    legend: {
      show: false,
      position: "bottom",
    },
  };

  private async saveProcess(result: Contract) {
    try {
      result.measuringPointId = result.measuringPoint ? result.measuringPoint.id : 0;
      result.start = this.service.setStartOfDay(result.start);
      result.end = this.service.setEndOfDay(result.end)
      this.service.validate(result, true);
      result = await lastValueFrom(this.service.repo.save(result));
      this.models.push(result);
      this.chart.refreshSeries();
    } catch (e: any) {
      this.service.validationErrorHandler(e);
    }

  }

  private isLock() {
    return (row: any) => {
      return row.locked && this.hasPermission;
    };
  }

  private isDeletable() {
    return (row: any) => {
      return this.hasPermission && (new Date(row.end)).getTime() > (new Date()).getTime();
    };
  }

  private unlock() {
    return async (row: any) => {
      await lastValueFrom(this.service.adminUnlock(row.id));
      this.loadData();
    };
  }

  public readonly GANT_FILTER_COOKIE_KEY = GANT_FILTER_COOKIE_KEY;

  onOnlyActiveChange(value: boolean): void {
    this.onlyActive = value;
    if (value) {
      this.tableActiveAt = null;
    }
    this.loadContracts();
  }

  onTableActiveAtChange(): void {
    this.loadContracts();
  }

  protected onChangeView($event: VIEW_CHANGER_CONTAINER) {
    this.viewChangers = $event;
    if ($event['calendarView']?.show) {

    } else {

    }
  }
}

